import { Alert, Dialog } from "@mui/material";
import { useCallback } from "react";
import type { ApiResponse, BooleanOperationResult } from "@entrilia/shared/api/types.ts";
import PromptDialogHeader from "@entrilia/shared/components/dialog/PromptDialogHeader.tsx";
import LoaderBox from "@entrilia/shared/components/LoaderBox.tsx";
import useFetch from "@entrilia/shared/hooks/useFetch.ts";
import { defined } from "@entrilia/shared/utilities/typeHelper.ts";
import type { ReportInfo, UpdateReportsAuthorizationRequest } from "../../../../../../../../../api/types/svTypes.ts";
import { useBiApiClientProvider } from "../../../../../contexts/BiApiClientProviderContext.ts";
import type { CurrentUser, ReportCompany } from "../ShareReport.types.ts";
import ShareReportsBulkContent from "./ShareReportsBulkContent.tsx";

interface Props {
  reports: ReportInfo[];
  currentUser: CurrentUser;
  reportCompany: ReportCompany;
  doUpdateSharing: (request: UpdateReportsAuthorizationRequest) => Promise<ApiResponse<BooleanOperationResult>>;
  onShared: () => void;
  onClose: () => void;
}

const ShareReportsBulk = ({ reports, currentUser, reportCompany, doUpdateSharing, onShared, onClose }: Props) => {
  const { getReportSharing } = useBiApiClientProvider();

  const firstReportId = defined(reports[0]).reportId;
  const getSharingOptions = useCallback(() => getReportSharing(firstReportId), [firstReportId, getReportSharing]);
  const [data, error, { isFetching }] = useFetch(getSharingOptions);

  return (
    <Dialog
      open={true}
      maxWidth="sm"
      fullWidth
      sx={{ ".MuiDialog-container": { alignItems: "start", pt: 20 } }}
      slotProps={{ paper: { sx: { minHeight: 250 } } }}
    >
      <PromptDialogHeader text={`Share ${reports.length} reports`} onClose={onClose} />
      <LoaderBox loading={isFetching} overlay>
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            Failed to load the sharing options.
          </Alert>
        )}
        {data && !error && (
          <ShareReportsBulkContent
            reports={reports}
            currentUser={currentUser}
            company={reportCompany}
            users={data.users}
            groups={data.groups}
            doUpdateSharing={doUpdateSharing}
            onShared={onShared}
            onClose={onClose}
          />
        )}
      </LoaderBox>
    </Dialog>
  );
};

export default ShareReportsBulk;
